// P9 — Delhi NCR geo config, slugs and URL helpers.

export const CITY_SLUG = 'delhi-ncr';
export const CITY_NAME = 'Delhi NCR';

// Area slug -> display name + approx centroid (lat/lng, 2dp) for "nearest area".
export const AREAS = [
  { slug: 'gurugram', name: 'Gurugram', lat: 28.46, lng: 77.03 },
  { slug: 'south-delhi', name: 'South Delhi', lat: 28.52, lng: 77.21 },
  { slug: 'noida', name: 'Noida', lat: 28.57, lng: 77.32 },
  { slug: 'dwarka', name: 'Dwarka', lat: 28.59, lng: 77.05 },
  { slug: 'faridabad', name: 'Faridabad', lat: 28.41, lng: 77.31 },
  { slug: 'ghaziabad', name: 'Ghaziabad', lat: 28.67, lng: 77.45 },
  { slug: 'greater-noida', name: 'Greater Noida', lat: 28.47, lng: 77.50 },
  { slug: 'indirapuram', name: 'Indirapuram', lat: 28.64, lng: 77.37 },
  { slug: 'rohini', name: 'Rohini', lat: 28.74, lng: 77.07 },
  { slug: 'west-delhi', name: 'West Delhi', lat: 28.66, lng: 77.10 },
  { slug: 'east-delhi', name: 'East Delhi', lat: 28.63, lng: 77.29 },
  { slug: 'north-delhi', name: 'North Delhi', lat: 28.70, lng: 77.21 },
  { slug: 'central-delhi', name: 'Central Delhi', lat: 28.64, lng: 77.22 },
];

export const AREA_SLUGS = AREAS.map((a) => a.slug);
// R3 — 'Delhi' is a city card (popular areas, ?area=delhi), not a pickable zone.
export const AREA_BY_SLUG = [...AREAS, { slug: 'delhi', name: 'Delhi' }].reduce((m, a) => ((m[a.slug] = a), m), {});

export const ALL_AREAS_LABEL = 'All of Delhi NCR';

// Zones list their whole city until artists carry a zone (backend routes/artists.py ZONE_CITY).
export const ZONE_CITY = {
  'south-delhi': 'Delhi', dwarka: 'Delhi', rohini: 'Delhi', 'west-delhi': 'Delhi', 'east-delhi': 'Delhi',
  'north-delhi': 'Delhi', 'central-delhi': 'Delhi', indirapuram: 'Ghaziabad',
};
// Heading place phrase: 'in Gurugram', 'across Delhi (Rohini and nearby)', 'in Delhi NCR';
// filled (R8: under 12 own artists, rest of NCR added): 'in Gurugram and nearby'.
export const inArea = (slug, filled = false) => {
  const name = AREA_BY_SLUG[slug]?.name;
  if (!name) return 'in Delhi NCR';
  if (filled) return `in ${name} and nearby`;
  return ZONE_CITY[slug] ? `across ${ZONE_CITY[slug]} (${name} and nearby)` : `in ${name}`;
};

// Category slug -> value sent to the API's `category` (regex, case-insensitive).
export const CATEGORY_SLUGS = {
  singers: 'Singer',
  djs: 'DJ',
  bands: 'Band',
  comedians: 'Comedian',
  dancers: 'Dancer',
  instrumentalists: 'Instrumentalist',
  magicians: 'Magician',
  anchors: 'Anchor',
  'mehendi-artists': 'Mehendi',
  'tattoo-artists': 'Tattoo',
  bartenders: 'Bartender',
  orchestras: 'Orchestra',
  percussionists: 'Percussion',
};

// R4 — artist-type filter (?type=a,b). Slugs match the category pages.
export const TYPE_OPTIONS = [
  { slug: 'singers', label: 'Singers' },
  { slug: 'djs', label: 'DJs' },
  { slug: 'bands', label: 'Bands' },
  { slug: 'instrumentalists', label: 'Instrumentalists' },
  { slug: 'percussionists', label: 'Dhol & percussion' },
  { slug: 'anchors', label: 'Anchors' },
  { slug: 'comedians', label: 'Comedians' },
  { slug: 'dancers', label: 'Dancers' },
  { slug: 'magicians', label: 'Magicians' },
  { slug: 'orchestras', label: 'Orchestras' },
];
export const TYPE_LABEL = TYPE_OPTIONS.reduce((m, t) => ((m[t.slug] = t.label), m), {});

// Plural category label for headings, crumbs and shelf titles ('Singers in Delhi NCR');
// CATEGORY_SLUGS stays singular because it is the stored category value.
export const CATEGORY_PLURAL = {
  ...TYPE_LABEL,
  'mehendi-artists': 'Mehendi artists',
  'tattoo-artists': 'Tattoo artists',
  bartenders: 'Bartenders',
};

// R5 — singular links (/delhi-ncr/band) redirect to the plural category page.
export const SINGULAR_TYPE = {
  band: 'bands', dj: 'djs', singer: 'singers', comedian: 'comedians', dancer: 'dancers',
  instrumentalist: 'instrumentalists', magician: 'magicians', anchor: 'anchors', orchestra: 'orchestras',
  percussionist: 'percussionists', bartender: 'bartenders', 'tattoo-artist': 'tattoo-artists',
  'mehendi-artist': 'mehendi-artists',
};

// R5 — occasion display labels (mirror backend services/occasion_service.OCCASION_LABELS).
export const OCCASION_LABELS = {
  'kitty-party': 'Kitty party',
  'kids-birthday': "Kids' birthday",
  'bhajan-satsang': 'Bhajan / satsang',
  'house-party': 'House party',
  'baby-shower': 'Baby shower',
  anniversary: 'Anniversary',
  'haldi-mehndi': 'Haldi & mehndi',
  'society-rwa': 'Society / RWA event',
  office: 'Office party',
  sangeet: 'Sangeet',
  'wedding-reception': 'Wedding reception',
  'engagement-roka': 'Engagement / roka',
  'cocktail-party': 'Cocktail party',
  'corporate-annual-day': 'Corporate / annual day',
  'diwali-party': 'Diwali party',
  lohri: 'Lohri',
  'holi-party': 'Holi party',
  'new-year-party': 'New Year party',
  'jagran-mata-ki-chowki': 'Jagran / mata ki chowki',
};
export const occasionLabel = (slug) => OCCASION_LABELS[slug]
  || String(slug || '').replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// '/delhi-ncr/band-in-noida' segment -> 'bands-in-noida' (null when already canonical).
export function pluralSegment(seg) {
  if (!seg) return null;
  const idx = seg.lastIndexOf('-in-');
  const hasArea = idx > 0 && AREA_SLUGS.includes(seg.slice(idx + 4));
  const left = hasArea ? seg.slice(0, idx) : seg;
  const plural = SINGULAR_TYPE[left];
  return plural ? `${plural}${hasArea ? seg.slice(idx) : ''}` : null;
}

export const BUDGET_BANDS = [
  { value: 'under-10k', label: 'Under ₹10k', min: null, max: 10000 },
  { value: '10k-25k', label: '₹10k – ₹25k', min: 10000, max: 25000 },
  { value: '25k-50k', label: '₹25k – ₹50k', min: 25000, max: 50000 },
  { value: 'above-50k', label: 'Above ₹50k', min: 50000, max: null },
];

export const RATING_OPTIONS = [
  { value: '4', label: '4.0+' },
  { value: '4.5', label: '4.5+' },
  { value: '4.8', label: '4.8+' },
];

export const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'rating', label: 'Top rated' },
  { value: 'enquiries', label: 'Most enquired' },
  { value: 'price-asc', label: 'Price: low to high' },
  { value: 'price-desc', label: 'Price: high to low' },
  { value: 'newest', label: 'Newest' },
];

// Parse a `/delhi-ncr/{segment}` single segment into pre-filters.
export function parseCitySegment(seg) {
  if (!seg) return {};
  let left = seg;
  let area = null;
  const idx = seg.lastIndexOf('-in-');
  if (idx > 0) {
    const maybeArea = seg.slice(idx + 4);
    if (AREA_SLUGS.includes(maybeArea)) {
      left = seg.slice(0, idx);
      area = maybeArea;
    }
  }
  if (CATEGORY_SLUGS[left]) {
    return { category: CATEGORY_SLUGS[left], categorySlug: left, area };
  }
  return { occasionSlug: left, area };
}

export function nearestArea(lat, lng) {
  let best = null;
  let bestD = Infinity;
  for (const a of AREAS) {
    const d = (a.lat - lat) ** 2 + (a.lng - lng) ** 2;
    if (d < bestD) {
      bestD = d;
      best = a;
    }
  }
  return best;
}
